import PropTypes, { number, func } from "prop-types"
import Icon from "components/planning_center_icon"
import { css } from "@emotion/core"

const LEFT = 1
const RIGHT = 1
const WINDOW = 2
// Maximum amount of pages links rendered will be LEFT + WINDOW + 1 + WINDOW + RIGHT

const Paginate = ({ pagination, onNavClick, isMobile, baseParams }) => {
  const handleNavClick = page => {
    return onNavClick({
      ...baseParams,
      page
    })
  }

  const pages = pagination.totalPages
    ? [...Array(pagination.totalPages).keys()].map(p => p + 1)
    : []

  if (pages.length <= 1) {
    return null
  }

  const left = pages.slice(0, LEFT)
  const right = pages.slice(-RIGHT)
  const CLIENT_ADJ_WINDOW = isMobile ? 1 : WINDOW
  const middle = pages.slice(
    pagination.currentPage - CLIENT_ADJ_WINDOW <= LEFT
      ? LEFT
      : Math.max(
          Math.min(
            ...[
              pagination.currentPage - CLIENT_ADJ_WINDOW - 1,
              pages.length - RIGHT - CLIENT_ADJ_WINDOW * 2 - 1
            ]
          ),
          LEFT
        ),
    pagination.currentPage + CLIENT_ADJ_WINDOW - 1 >= pages.length - RIGHT
      ? -RIGHT
      : Math.min(
          Math.max(
            ...[pagination.currentPage + CLIENT_ADJ_WINDOW, LEFT + CLIENT_ADJ_WINDOW * 2 + 1]
          ),
          pages.length - 1
        )
  )

  const leftGap = left.slice(-1)[0] + 1 < middle[0] // is the last page on the left + 1 less than the first page in the middle window?
  const rightGap = middle.slice(-1)[0] + 1 < right[0] // is the last page in the middle window + 1 less than the first page on the right?

  return (
    <div className="pagination">
      <PrevPageButton prevPage={pagination.prevPage} onPrevPageClick={handleNavClick} />

      {left.map(page => (
        <PageLink
          key={page}
          page={page}
          currentPage={pagination.currentPage}
          onPageLinkClick={handleNavClick}
        />
      ))}
      {leftGap && <GapEllipsis />}
      {middle.map(page => (
        <PageLink
          key={page}
          page={page}
          currentPage={pagination.currentPage}
          onPageLinkClick={handleNavClick}
        />
      ))}
      {rightGap && <GapEllipsis />}
      {right.map(page => (
        <PageLink
          key={page}
          page={page}
          currentPage={pagination.currentPage}
          onPageLinkClick={handleNavClick}
        />
      ))}

      <NextPageButton nextPage={pagination.nextPage} onNextPageClick={handleNavClick} />
    </div>
  )
}

Paginate.propTypes = {
  pagination: PropTypes.shape({
    nextPage: PropTypes.number,
    prevPage: PropTypes.number,
    totalPages: PropTypes.number,
    currentPage: PropTypes.number
  }),
  baseParams: PropTypes.shape({}),
  onNavClick: PropTypes.func,
  isMobile: PropTypes.bool,
  styles: PropTypes.shape({})
}

const GapEllipsis = () => {
  return (
    <span className="page m-1" style={{ fontSize: "x-large" }}>
      ...
    </span>
  )
}

const NextPageButton = ({ onNextPageClick, nextPage }) => {
  return nextPage ? (
    <button
      className="btn-ghost sm pagination__next-link"
      aria-label="next page"
      onClick={() => onNextPageClick(nextPage)}
    >
      <Icon symbol="general#right-chevron" />
    </button>
  ) : null
}

NextPageButton.propTypes = {
  nextPage: number,
  onNextPageClick: func
}

const PrevPageButton = ({ onPrevPageClick, prevPage }) => {
  return prevPage ? (
    <button
      className="btn-ghost sm pagination__previous-link"
      aria-label="pervious page"
      onClick={() => onPrevPageClick(prevPage)}
    >
      <Icon symbol="general#left-chevron" />
    </button>
  ) : null
}

PrevPageButton.propTypes = {
  prevPage: number,
  onPrevPageClick: func
}

const PageLink = ({ page, currentPage, onPageLinkClick }) => {
  const isCurrent = page === currentPage

  return (
    <span className={`page ${isCurrent ? "current" : ""}`}>
      {isCurrent && page}
      {!isCurrent && (
        <button onClick={() => onPageLinkClick(page)} rel="next">
          {page}
        </button>
      )}
    </span>
  )
}

PageLink.propTypes = {
  page: number,
  currentPage: number,
  onPageLinkClick: func
}

export default Paginate
